/**
 * @file 应用启动入口
 * @description 初始化 Vue 应用、Pinia、路由、主题和生产环境错误上报。
 */
import { createApp } from 'vue'

import App from './App.vue'
import router from './router'
import pinia from '@/stores/index'
import { grayReleaseStore } from '@/stores'
import axios from 'axios'
import { initThemeBeforeMount } from '@/composables/useTheme'
import { recoverChunkLoadFailure } from '@/utils/chunkLoadRecovery'
import { startReleaseInvalidationMonitor } from '@/utils/releaseInvalidation'
import { shouldIgnoreSentryEvent } from '@/utils/expectedErrors'

import 'element-plus/theme-chalk/dark/css-vars.css'
import 'uno.css'
import '@/assets/styles/theme/index.scss'
import '@/assets/main.scss'

const app = createApp(App)

axios.defaults.baseURL = '/api'

app.use(pinia)
initThemeBeforeMount()
grayReleaseStore().bindLoginState()
app.use(router)

// Vite 预加载 chunk 失败（部署后 hash 变更）时按路径自动刷新一次
window.addEventListener('vite:preloadError', (event) => {
  if (recoverChunkLoadFailure(event)) {
    event.preventDefault()
  }
})

// 动态 import Promise 拒绝时按路径自动刷新一次
window.addEventListener('unhandledrejection', (event) => {
  if (recoverChunkLoadFailure(event.reason)) {
    event.preventDefault()
  }
})

// 脚本加载错误冒泡到全局时按路径自动刷新一次
window.addEventListener('error', (event) => {
  if (recoverChunkLoadFailure(event.error || event.message)) {
    event.preventDefault()
  }
})

/** Bugsink/Sentry DSN，优先 VITE_BUGSINK_DSN；无 DSN 时不上报 */
const sentryDsn =
  import.meta.env.VITE_BUGSINK_DSN || import.meta.env.VITE_SENTRY_DSN || ''

/** 初始化错误上报 SDK，放到空闲期加载以避开首屏关键路径 */
const initSentry = async (): Promise<void> => {
  const Sentry = await import('@sentry/vue')
  Sentry.init({
    app,
    dsn: sentryDsn,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    beforeSend(event, hint) {
      if (event.type === 'transaction') return null
      if (shouldIgnoreSentryEvent(event, hint)) return null
      if (event.exception?.values?.length) return event
      if (event.level === 'error' || event.level === 'fatal') return event
      return null
    }
  })
}

/** 调度错误上报初始化，避免监控代码阻塞首屏渲染 */
const scheduleSentryInit = (): void => {
  const run = (): void => {
    void initSentry()
  }
  window.setTimeout(() => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 3000 })
      return
    }
    run()
  }, 6000)
}

if (sentryDsn && import.meta.env.PROD) {
  scheduleSentryInit()
}
app.mount('#app')

// 生产环境轮询构建版本，新版本部署后使已打开的旧页面自动失效。
if (import.meta.env.PROD) {
  startReleaseInvalidationMonitor()
}
